import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Stack,
  Paper,
  Divider,
  Pagination,
} from '@mui/material';
import {
  History,
  CalendarToday,
  MedicalServices,
  CleaningServices,
  LocalDrink,
  Assignment,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';

export interface HistoryEntry {
  data: string;
  tipo_manejo: string;
  detalhes: string;
}

export interface AnimalHistoryData {
  api_status: string;
  api_message: string;
  operation_details: {
    entity_type: string;
    identifier: string;
    historico: HistoryEntry[];
  };
  entity_profile: {
    _key: string;
    _id: string;
    _rev: string;
    farm_id: string;
    current_farm_id: string;
    birth_farm_id: string;
    name: string;
    cattle_id: {
      tag: string;
      country_code: string;
      manufacturer_code: number;
      unique_id: string;
    };
    breed_code: string;
    gender: string;
    birth_date: string;
    birth_weight_kg: number;
    production_purpose: string | null;
    dam_cattle_id: string | null;
    sire_cattle_id: string | null;
    current_paddock_id: string;
    death_date: string | null;
    death_reason: string | null;
    current_status: string;
    created_at: string;
    updated_at: string;
  };
}

export interface AnimalHistoryDetailsData {
  data: AnimalHistoryData;
  resumo: string;
  url: string;
}

export interface AnimalHistoryViewProps {
  animalHistoryData: AnimalHistoryDetailsData;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR');
};

const getStatusColor = (status: string, t: any) => {
  return status === t('animal.status_values.active') ? DESIGN_TOKENS.colors.brand.primary : DESIGN_TOKENS.colors.status.error[500];
};

const getManagementTypeLabel = (tipoManejo: string, t: any) => {
  const labels: Record<string, string> = {
    'diagnostico_mastite': t('animal.management_types.mastitis_diagnosis'),
    'aplicacao_ocitocina': t('animal.management_types.oxytocin_application'),
    'lavagem_ubere': t('animal.management_types.udder_washing'),
    'registrar_manejo': t('animal.management_types.management_record'),
    'limpeza_tetos': t('animal.management_types.teat_cleaning'),
    'vacinacao': t('animal.management_types.vaccination'),
    'medicacao': t('animal.management_types.medication'),
    'inseminacao': t('animal.management_types.insemination'),
    'pesagem': t('animal.management_types.weighing'),
  };
  return labels[tipoManejo] || tipoManejo;
};

const getManagementIcon = (tipoManejo: string) => {
  switch (tipoManejo) {
    case 'diagnostico_mastite':
    case 'medicacao':
    case 'vacinacao':
      return <MedicalServices sx={{ fontSize: 20, color: 'white' }} />;
    case 'aplicacao_ocitocina':
      return (
        <Box
          sx={{
            width: 20,
            height: 20,
            bgcolor: 'white',
            maskImage: 'url(/Leite.svg)',
            WebkitMaskImage: 'url(/Leite.svg)',
            maskRepeat: 'no-repeat',
            WebkitMaskRepeat: 'no-repeat',
            maskSize: 'contain',
            WebkitMaskSize: 'contain',
            maskPosition: 'center',
            WebkitMaskPosition: 'center',
          }}
        />
      );
    case 'lavagem_ubere':
    case 'limpeza_tetos':
      return <CleaningServices sx={{ fontSize: 20, color: 'white' }} />;
    case 'registrar_manejo':
    default:
      return <Assignment sx={{ fontSize: 20, color: 'white' }} />;
  }
};

const getManagementColor = () => {
  return DESIGN_TOKENS.colors.brand.primary;
};

const AnimalHistoryView: React.FC<AnimalHistoryViewProps> = ({
  animalHistoryData,
}) => {
  const { t } = useTranslation();
  const { data, resumo } = animalHistoryData;
  const { operation_details, entity_profile } = data;
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  
  // Calculate pagination
  const totalItems = operation_details.historico.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  
  // Get current page items
  const currentItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return operation_details.historico.slice(startIndex, endIndex);
  }, [operation_details.historico, currentPage, itemsPerPage]);
  
  // Handle page change
  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Avatar
          sx={{
            width: 80,
            height: 80,
            bgcolor: DESIGN_TOKENS.colors.brand.primary,
            mx: 'auto',
            mb: 2,
            fontSize: '2rem',
          }}
        >
          <History sx={{ fontSize: '2rem' }} />
        </Avatar>
        <Typography variant="h4" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.brand.primary, mb: 1 }}>
          {t('animal.history.title')}
        </Typography>
        <Typography variant="body1" sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 2 }}>
          {resumo}
        </Typography>
      </Box>

      {/* Animal Info Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <img 
                src="/vaca.png" 
                alt="Animal" 
                style={{ width: 24, height: 24, filter: 'brightness(0) invert(1)' }}
              />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t('animal.name')}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {entity_profile.name}
                </Typography>
              </Box>
            </Box>
            
            <Box>
              <Typography variant="body2" color="text.secondary">
                {t('animal.breed')}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, color: DESIGN_TOKENS.colors.brand.primary }}>
                {entity_profile.breed_code}
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="body2" color="text.secondary">
                {t('animal.status')}
              </Typography>
              <Chip
                label={entity_profile.current_status}
                size="small"
                sx={{
                  bgcolor: getStatusColor(entity_profile.current_status, t),
                  color: 'white',
                  fontWeight: 600,
                  '& .MuiChip-icon': {
                    color: 'white'
                  }
                }}
              />
            </Box>
            
            <Box>
              <Typography variant="body2" color="text.secondary">
                {t('animal.history.total_records')}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {operation_details.historico.length}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Timeline */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: DESIGN_TOKENS.colors.text.primary }}>
            {t('animal.history.timeline_title')}
          </Typography>
          
          {totalItems === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                {t('animal.history.no_records_found')}
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ position: 'relative' }}>
                {currentItems.map((entry, index) => (
                  <Box key={index} sx={{ display: 'flex', mb: 3, position: 'relative' }}>
                    {/* Timeline Dot */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mr: 2 }}>
                      <Avatar
                        sx={{ 
                          bgcolor: getManagementColor(),
                          width: 40,
                          height: 40,
                        }}
                      >
                        {getManagementIcon(entry.tipo_manejo)}
                      </Avatar>
                      {index < currentItems.length - 1 && (
                        <Box 
                          sx={{ 
                            width: 2, 
                            height: 40, 
                            bgcolor: DESIGN_TOKENS.colors.neutral[300],
                            mt: 1,
                          }} 
                        />
                      )}
                    </Box>
                    
                    {/* Timeline Content */}
                    <Box sx={{ flex: 1, pt: 0.5 }}>
                      <Paper 
                        elevation={1} 
                        sx={{ 
                          p: 2, 
                          bgcolor: DESIGN_TOKENS.colors.background.paper,
                          border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              fontWeight: 600, 
                              color: getManagementColor(),
                              fontSize: '1rem',
                            }}
                          >
                            {getManagementTypeLabel(entry.tipo_manejo, t)}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CalendarToday sx={{ fontSize: 16, color: DESIGN_TOKENS.colors.text.secondary }} />
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: DESIGN_TOKENS.colors.text.secondary,
                                fontWeight: 500,
                              }}
                            >
                              {formatDate(entry.data)}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: DESIGN_TOKENS.colors.text.primary,
                            lineHeight: 1.5,
                          }}
                        >
                          {entry.detalhes}
                        </Typography>
                      </Paper>
                    </Box>
                  </Box>
                ))}
              </Box>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size="medium"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AnimalHistoryView;